import { useMemo, useState } from "preact/hooks";
import { useCardAIResults } from "./store";
import PsychologyIcon from "@mui/icons-material/Psychology";
import {
  Box, Card, CardContent, Typography, Chip,
  CircularProgress, Divider, List, ListItem, ListItemText, Collapse, Button,
  Tabs, Tab, IconButton, Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';

const StyledCardAIResults = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  borderRadius: 8,
  overflow: "hidden",
}));

function Markdown({ value = "" }) {
  const [MD, setMD] = useState(null);
  if (!MD) {
    import("react-markdown").then(m => setMD(() => m.default)).catch(() => {});
    return <pre style={{ whiteSpace: "pre-wrap" }}>{String(value)}</pre>;
  }
  const Comp = MD;
  return <Comp>{String(value)}</Comp>;
}

function AudioPlayer({ text }) {
  const { ttsLoading, ttsPlaying, ttsError, handleTTSPlayPause } = useCardAIResults();

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Tooltip title={ttsError || (ttsLoading ? "Generando audio..." : ttsPlaying ? "Pausar" : "Escuchar")}>
        <IconButton 
          onClick={() => handleTTSPlayPause(text)} 
          color={ttsPlaying ? "secondary" : "primary"}
          disabled={ttsLoading || !!ttsError}
        >
          {ttsLoading ? <CircularProgress size={20} /> : ttsPlaying ? <StopIcon /> : <PlayArrowIcon />}
        </IconButton>
      </Tooltip>
      {ttsError && (
        <Typography variant="caption" color="error" sx={{ maxWidth: 200 }}>
          {ttsError}
        </Typography>
      )}
    </Box>
  );
}

function ReviewDisplay({ review }) {
  const [tab, setTab] = useState(0);
  const handleChange = (_, newValue) => setTab(newValue);

  const currentText = tab === 0 ? (review.resume || "Sin resumen disponible.") : (review.description || "Sin detalle técnico disponible.");

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs value={tab} onChange={handleChange} aria-label="ai review tabs" variant="standard">
          <Tab label="Resumen Ejecutivo" />
          <Tab label="Detalle Técnico" />
        </Tabs>
        {/* Solo mostrar AudioPlayer en tab 0 (Resumen Ejecutivo) */}
        {tab === 0 && (
          <Box sx={{ mr: 1 }}>
            <AudioPlayer text={currentText} />
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2, mt: 1, maxHeight: 400, overflowY: "auto", border: "1px solid", borderColor: "divider" }}>
        <Markdown value={currentText} />
      </Box>
    </Box>
  );
}

export const CardAIResultsComponent = () => {
  const {
    quotation, isLoading, error,
    statusName, isCreated, isInAIPhase,
    aiReviews, lastReview,
  } = useCardAIResults();

  if (!quotation) return null;
  if (isCreated)   return null;

  const hasAI = Array.isArray(aiReviews) && aiReviews.length > 0;
  const [showHistory, setShowHistory] = useState(false);
  const history = useMemo(() => (aiReviews || []).slice(1), [aiReviews]);

  const StatusChipEl = useMemo(() => {
    const label = statusName || "—";
    const color =
      statusName === "ai-review" ? "info" :
      statusName === "quoted"    ? "success" :
      "default";
    return <Chip size="small" label={label} color={color} />;
  }, [statusName]);

  return (
    <StyledCardAIResults elevation={1}>
      {/* Header primary */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 1.5,
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h6"
          color="primary.contrastText"
          sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}
        >
          <PsychologyIcon fontSize="small" sx={{ color: "primary.contrastText" }} />
          Análisis IA de requerimientos
        </Typography>
      </Box>
      <CardContent>
        {isLoading && (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Cargando cotización…</Typography>
          </Box>
        )}

        {!isLoading && error && (
          <Typography variant="body2" color="error">
            Ocurrió un error al cargar la cotización.
          </Typography>
        )}

        {!isLoading && !error && isInAIPhase && !hasAI && (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Generando el análisis con IA… Se mostrará aquí en cuanto esté listo.
            </Typography>
          </Box>
        )}

        {!isLoading && !error && hasAI && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Último análisis
            </Typography>
            
            <ReviewDisplay review={lastReview} />

            {aiReviews.length > 1 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  startIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setShowHistory(v => !v)}
                >
                  {showHistory ? "Ocultar historial" : "Ver historial"}
                </Button>
                <Collapse in={showHistory} unmountOnExit>
                  <List dense sx={{ mt: 1 }}>
                    {history.map((r) => {
                      const id = r._id || r.id;
                      const when = new Date(r.createdAt || r.created_at || Date.now()).toLocaleString();
                      return (
                        <ListItem key={id} alignItems="flex-start" disableGutters>
                          <ListItemText
                            primary={<Typography variant="subtitle2">{when}</Typography>}
                            secondary={<Box sx={{ mt: 0.5 }}><ReviewDisplay review={r} /></Box>}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            )}
          </Box>
        )}


        {!isLoading && !error && !isInAIPhase && !hasAI && (
          <Typography variant="body2" color="text.secondary">
            Aún no hay análisis de IA para esta cotización.
          </Typography>
        )}
      </CardContent>
    </StyledCardAIResults>
  );
};
